import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import axios from 'axios'
import {
  ArrowLeft,
  CheckCircle2,
  Copy,
  Download,
  Film,
  Folder,
  FolderInput,
  HardDrive,
  Image as ImageIcon,
  ListChecks,
  Loader2,
  Plus,
  Search,
  Trash2,
  Upload,
  User,
  Users,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Switch } from '../components/ui/switch'
import { Label } from '../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog'
import BrandLogo from '../components/BrandLogo'
import DashboardButton from '../components/DashboardButton'
import LanguageToggle from '../components/LanguageToggle'
import ThemeMenu from '../components/ThemeMenu'
import ProfileButton from '../components/ProfileButton'
import AppFooter from '../components/AppFooter'
import { useLanguage } from '../contexts/LanguageContext'
import { formatMoney, getApiUrl } from '../lib/utils'
import { formatBytes } from '../lib/media'
import {
  MEDIA_ACCEPT,
  MEDIA_UNSORTED,
  MediaFolder,
  MediaItem,
  MediaStatus,
  createArchiveLink,
  deleteEventMedia,
  getMediaStatus,
  hostUploadHandlers,
  listEventMedia,
  moveMedia,
  prepareFile,
  purchaseMediaStorage,
  updateMediaSettings,
  uploadPrepared,
} from '../lib/media'

const API = getApiUrl()
const PAGE_SIZE = 60

/**
 * /events/:eventId/media — the organizer's album for the R2-backed guest
 * media vault: everything guests (and the host) uploaded straight to the
 * bucket, plus the vault controls: guest-link toggle + copy, host uploads,
 * per-item and download-all (zip). Files are served via short-lived signed URLs.
 */
const MediaGallery = () => {
  const { eventId } = useParams()
  const { language } = useLanguage()
  const tr = (en: string, ka: string) => (language === 'ka' ? ka : en)

  const [eventTitle, setEventTitle] = useState('')
  const [inviteLink, setInviteLink] = useState('')
  const [status, setStatus] = useState<MediaStatus | null>(null)
  const [togglingShare, setTogglingShare] = useState(false)
  const [tab, setTab] = useState<'photo' | 'video'>('photo')
  const [uploadedBy, setUploadedBy] = useState<'all' | 'host' | 'guest'>('all')
  const [sort, setSort] = useState<'newest' | 'oldest' | 'largest' | 'uploader'>('newest')
  const [searchText, setSearchText] = useState('')
  const [query, setQuery] = useState('') // debounced searchText
  const [items, setItems] = useState<MediaItem[]>([])
  const [total, setTotal] = useState(0)
  const [photoCount, setPhotoCount] = useState(0)
  const [videoCount, setVideoCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [lightbox, setLightbox] = useState<MediaItem | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [uploadState, setUploadState] = useState<{ done: number; total: number; percent: number } | null>(null)
  const [archiving, setArchiving] = useState(false)
  const [purchasing, setPurchasing] = useState<string | null>(null)
  // Storage is a flat per-GB rate, so the only decision is how much. Starts at
  // the server's default_gb (seeded on first load) rather than a magic number.
  const [buyGb, setBuyGb] = useState(100)
  const touchedGb = useRef(false)
  const [folderFilter, setFolderFilter] = useState<string | null>(null) // null=all, MEDIA_UNSORTED=unsorted, else name
  const [folders, setFolders] = useState<MediaFolder[]>([])
  const [unsortedCount, setUnsortedCount] = useState(0)
  const [selectMode, setSelectMode] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [moving, setMoving] = useState(false)
  const [showMoveMenu, setShowMoveMenu] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<any>(null)

  useEffect(() => {
    axios
      .get(`${API}/events/${eventId}`, { withCredentials: true })
      .then((res) => {
        setEventTitle(res.data?.title || '')
        setInviteLink(res.data?.invite_link || '')
      })
      .catch(() => {})
    getMediaStatus(eventId!)
      .then((s) => {
        setStatus(s)
        // Pre-fill the amount with the server's default the first time we learn
        // it, so the figure is configurable rather than hardcoded — but never
        // stomp a number the host has already typed.
        if (s.default_gb && !touchedGb.current) setBuyGb(s.default_gb)
      })
      .catch(() => {})
  }, [eventId])

  // Debounce typing → query
  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setQuery(searchText), 350)
    return () => clearTimeout(debounceRef.current)
  }, [searchText])

  const fetchPage = useCallback(
    async (offset: number, append: boolean) => {
      const page = await listEventMedia(eventId!, {
        media: tab,
        uploaded_by: uploadedBy,
        q: query || undefined,
        sort,
        folder: folderFilter ?? undefined,
        limit: PAGE_SIZE,
        offset,
      })
      setTotal(page.total)
      setPhotoCount(page.photo_count)
      setVideoCount(page.video_count)
      setHasMore(page.has_more)
      setFolders(page.folders)
      setUnsortedCount(page.unsorted_count)
      setItems((prev) => (append ? [...prev, ...page.items] : page.items))
    },
    [eventId, tab, uploadedBy, query, sort, folderFilter],
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchPage(0, false)
      .catch(() => {
        if (!cancelled) toast.error(tr('Could not load the album', 'ალბომი ვერ ჩაიტვირთა'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage])

  const loadMore = async () => {
    setLoadingMore(true)
    try {
      await fetchPage(items.length, true)
    } catch {
      toast.error(tr('Could not load more', 'მეტის ჩატვირთვა ვერ მოხერხდა'))
    } finally {
      setLoadingMore(false)
    }
  }

  const shareUrl = inviteLink ? `${window.location.origin}/invite/${inviteLink}/share` : ''

  const toggleGuestUploads = async (enabled: boolean) => {
    setTogglingShare(true)
    try {
      setStatus(await updateMediaSettings(eventId!, enabled))
      toast.success(
        enabled
          ? tr('Guests can now upload via the share link', 'სტუმრებს უკვე შეუძლიათ ატვირთვა ბმულით')
          : tr('Guest uploads are off', 'სტუმრების ატვირთვები გამორთულია'),
      )
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || tr('Could not update the setting', 'პარამეტრი ვერ შეიცვალა'))
    } finally {
      setTogglingShare(false)
    }
  }

  const copyShareLink = async () => {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      toast.success(tr('Share link copied', 'გასაზიარებელი ბმული დაკოპირდა'))
    } catch {
      toast.error(tr('Could not copy the link', 'ბმულის კოპირება ვერ მოხერხდა'))
    }
  }

  const buyStorage = async (key: string, body: { gb?: number }) => {
    setPurchasing(key)
    try {
      const res = await purchaseMediaStorage(eventId!, body)
      if (res.checkout_url) {
        window.location.href = res.checkout_url // live gateway: go pay, then return
        return
      }
      // Sandbox: applied instantly — refresh the quota/usage state.
      const fresh = await getMediaStatus(eventId!)
      setStatus(fresh)
      // The amount field is shared with the top-up row, so put it back to the
      // default; otherwise the next top-up silently inherits what was just
      // bought (buy 250 GB → "Add more" pre-filled with another 250).
      touchedGb.current = false
      if (fresh.default_gb) setBuyGb(fresh.default_gb)
      toast.success(tr('Storage added — your album is ready', 'საცავი დაემატა — ალბომი მზადაა'))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || tr('Could not complete the purchase', 'შენაძენი ვერ დასრულდა'))
    } finally {
      setPurchasing(null)
    }
  }

  const pricePerGb = status?.price_per_gb ?? 0.3
  const maxGb = status?.max_gb || 1000
  const clampGb = (n: number) => Math.max(1, Math.min(maxGb, Math.round(n)))

  const quota = status?.quota_bytes || 0
  const usedPct = quota > 0 ? Math.min(100, Math.round(((status?.used_bytes || 0) / quota) * 100)) : 0
  const daysLeft = status?.expires_at
    ? Math.max(0, Math.ceil((new Date(status.expires_at).getTime() - Date.now()) / 86_400_000))
    : null
  const deleteDate = status?.delete_at
    ? new Intl.DateTimeFormat(language === 'ka' ? 'ka-GE' : 'en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(new Date(status.delete_at))
    : null

  const handleHostFiles = async (list: FileList | null) => {
    if (!list || list.length === 0) return
    const files = Array.from(list)
    if (fileInputRef.current) fileInputRef.current.value = ''
    const handlers = hostUploadHandlers(eventId!)
    let ok = 0
    setUploadState({ done: 0, total: files.length, percent: 0 })
    for (const file of files) {
      try {
        const prepared = await prepareFile(file)
        await uploadPrepared(handlers, prepared, undefined, (fraction) => {
          setUploadState((s) => (s ? { ...s, percent: Math.round(fraction * 100) } : s))
        }, {
          maxImageBytes: status?.max_image_bytes,
          maxVideoBytes: status?.max_video_bytes,
        })
        ok += 1
      } catch (err: any) {
        toast.error(
          err?.response?.data?.detail ||
            err?.message ||
            tr(`Could not upload "${file.name}"`, `"${file.name}" ვერ აიტვირთა`),
        )
      }
      setUploadState((s) => (s ? { ...s, done: s.done + 1, percent: 0 } : s))
    }
    setUploadState(null)
    if (ok > 0) {
      toast.success(
        language === 'ka' ? `${ok} ფაილი აიტვირთა` : `${ok} file${ok > 1 ? 's' : ''} uploaded`,
      )
      setLoading(true)
      fetchPage(0, false).finally(() => setLoading(false))
      getMediaStatus(eventId!).then(setStatus).catch(() => {})
    }
  }

  const handleDownloadAll = async () => {
    setArchiving(true)
    try {
      const link = await createArchiveLink(eventId!, { media: tab, folder: folderFilter ?? undefined })
      if (!link.count) {
        toast.info(tr('Nothing to download here yet', 'ჩამოსატვირთი ჯერ არაფერია'))
        return
      }
      // The signed URL carries its own auth — a plain navigation streams the zip.
      window.location.href = link.url
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || tr('Could not start the download', 'ჩამოტვირთვა ვერ დაიწყო'))
    } finally {
      setArchiving(false)
    }
  }

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const clearSelection = () => {
    setSelected(new Set())
    setSelectMode(false)
    setShowMoveMenu(false)
  }

  const moveSelected = async (folder: string | null) => {
    if (selected.size === 0) return
    setMoving(true)
    try {
      const ids = Array.from(selected)
      await moveMedia(eventId!, ids, folder)
      toast.success(
        folder
          ? tr(`Moved ${ids.length} to "${folder}"`, `${ids.length} გადავიდა "${folder}"-ში`)
          : tr(`Moved ${ids.length} to Unsorted`, `${ids.length} დაუხარისხებელში`),
      )
      setShowMoveMenu(false)
      clearSelection()
      setLoading(true)
      await fetchPage(0, false).finally(() => setLoading(false))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || tr('Could not move the files', 'ფაილები ვერ გადავიდა'))
    } finally {
      setMoving(false)
    }
  }

  const downloadSelected = async () => {
    if (selected.size === 0) return
    setArchiving(true)
    try {
      const link = await createArchiveLink(eventId!, { media_ids: Array.from(selected) })
      if (!link.count) return
      window.location.href = link.url
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || tr('Could not start the download', 'ჩამოტვირთვა ვერ დაიწყო'))
    } finally {
      setArchiving(false)
    }
  }

  const handleDelete = async (item: MediaItem) => {
    if (!window.confirm(tr(
      'Remove this file from the album? This deletes it from storage permanently.',
      'წავშალოთ ეს ფაილი ალბომიდან? ის სამუდამოდ წაიშლება საცავიდან.',
    ))) return
    setDeleting(true)
    try {
      await deleteEventMedia(eventId!, item.media_id)
      setLightbox(null)
      setItems((prev) => prev.filter((m) => m.media_id !== item.media_id))
      setTotal((t) => Math.max(0, t - 1))
      if (item.content_type?.startsWith('video/')) setVideoCount((c) => Math.max(0, c - 1))
      else setPhotoCount((c) => Math.max(0, c - 1))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || tr('Could not delete.', 'წაშლა ვერ მოხერხდა.'))
    } finally {
      setDeleting(false)
    }
  }

  const formatWhen = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(language === 'ka' ? 'ka-GE' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
    return (iso: string | null) => (iso ? fmt.format(new Date(iso)) : '')
  }, [language])

  const tabs: Array<{ id: 'photo' | 'video'; label: string; count: number; icon: any }> = [
    { id: 'photo', label: tr('Photos', 'ფოტოები'), count: photoCount, icon: ImageIcon },
    { id: 'video', label: tr('Videos', 'ვიდეოები'), count: videoCount, icon: Film },
  ]

  return (
    <div className="min-h-screen bg-celebrate flex flex-col 2xl:[zoom:1.25]">
      {/* Header */}
      <header className="bg-white/80 dark:bg-[#1e1438]/80 backdrop-blur-xl border-b border-[#1b1033]/10 dark:border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link to={`/events/${eventId}`} className="mr-4">
              <Button
                variant="ghost"
                size="icon"
                className="rounded-xl text-[#1b1033] dark:text-[#f4f0ff] hover:bg-[#1b1033]/[0.05] hover:text-[#ff4d8d] transition-colors"
                data-testid="back-to-event"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <BrandLogo className="mr-4 hidden md:inline-flex" />
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-[#1b1033] dark:text-[#f4f0ff] font-display tracking-tight truncate">
                {tr('Guest album', 'სტუმრების ალბომი')}
                {eventTitle ? ` · ${eventTitle}` : ''}
              </h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 ml-4">
              <DashboardButton />
              <LanguageToggle />
              <ThemeMenu />
              <ProfileButton />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Paid storage plan — no free tier: uploads require an active plan */}
        {status && (
          <div className="glass-card rounded-bubble p-4 sm:p-6" data-testid="media-storage-card">
            {status.service_active ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-2xl bg-[#fdeee9] dark:bg-[#281b47] flex items-center justify-center">
                    <HardDrive className="w-5 h-5 text-[#ff4d8d]" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold tracking-tight text-[#1b1033] dark:text-[#f4f0ff] font-display">
                      {tr('Album storage', 'ალბომის საცავი')}
                    </h3>
                    <p className="text-xs text-[#4a3d66] dark:text-[#c3b8dd] mt-0.5">
                      {formatBytes(status.quota_bytes)}
                      {daysLeft !== null
                        ? ` · ${language === 'ka' ? `დარჩა ${daysLeft} დღე` : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}`
                        : ''}
                    </p>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-[#4a3d66] dark:text-[#c3b8dd] mb-1.5 tabular-nums">
                    <span>{formatBytes(status.used_bytes)} / {formatBytes(status.quota_bytes)}</span>
                    <span>{usedPct}%</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-[#1b1033]/10 dark:bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full btn-gradient transition-all" style={{ width: `${usedPct}%` }} data-testid="media-usage-bar" />
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-[#4a3d66] dark:text-[#c3b8dd]">{tr('Add more', 'დაამატეთ')}:</span>
                  <Input
                    type="number"
                    min={1}
                    max={maxGb}
                    value={buyGb}
                    onChange={(e) => { touchedGb.current = true; setBuyGb(clampGb(Number(e.target.value) || 1)) }}
                    className="w-20 h-9 rounded-full text-center"
                    data-testid="media-topup-gb"
                  />
                  <span className="text-sm text-[#4a3d66] dark:text-[#c3b8dd]">GB</span>
                  <Button
                    onClick={() => buyStorage('topup', { gb: clampGb(buyGb) })}
                    disabled={purchasing !== null}
                    className="btn-gradient text-white rounded-full h-9 px-5"
                    data-testid="media-topup-btn"
                  >
                    {purchasing === 'topup' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    {tr('Add', 'დამატება')} · {status.currency} {formatMoney(pricePerGb * clampGb(buyGb))}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-2xl bg-[#fdeee9] dark:bg-[#281b47] flex items-center justify-center">
                    <HardDrive className="w-5 h-5 text-[#ff4d8d]" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold tracking-tight text-[#1b1033] dark:text-[#f4f0ff] font-display">
                      {status.expires_at ? tr('Storage window ended', 'საცავის ვადა ამოიწურა') : tr('Add album storage', 'დაამატეთ ალბომის საცავი')}
                    </h3>
                    <p className="text-xs text-[#4a3d66] dark:text-[#c3b8dd] mt-0.5 max-w-lg">
                      {status.expires_at
                        ? tr('Renew to let guests add photos again.', 'განაახლეთ, რომ სტუმრებმა ისევ შეძლონ ატვირთვა.')
                        : language === 'ka'
                          ? `სტუმრები ვერ ატვირთავენ ფოტოებს, სანამ ადგილს არ შეიძენთ. ერთჯერადი გადახდა — ${status.service_days} დღე.`
                          : `Guests can't upload until you buy space. One-time payment, covers ${status.service_days} days.`}
                    </p>
                    {status.expires_at && deleteDate && (
                      <p className="text-xs text-amber-600 dark:text-amber-500 mt-1.5 font-medium" data-testid="media-delete-warning">
                        {language === 'ka'
                          ? `ფაილები დაცულია ${deleteDate}-მდე, შემდეგ სამუდამოდ წაიშლება.`
                          : `Photos are kept until ${deleteDate}, then permanently deleted.`}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="text-sm text-[#4a3d66] dark:text-[#c3b8dd]">
                    {tr('How much space?', 'რამდენი ადგილი?')}
                  </span>
                  <Input
                    type="number"
                    min={1}
                    max={maxGb}
                    value={buyGb}
                    onChange={(e) => { touchedGb.current = true; setBuyGb(clampGb(Number(e.target.value) || 1)) }}
                    className="w-24 h-9 rounded-full text-center"
                    data-testid="media-buy-gb"
                  />
                  <span className="text-sm text-[#4a3d66] dark:text-[#c3b8dd]">
                    GB · {status.currency} {formatMoney(pricePerGb)}/GB
                  </span>
                  <Button
                    onClick={() => buyStorage('buy', { gb: clampGb(buyGb) })}
                    disabled={purchasing !== null}
                    className="btn-gradient text-white rounded-full h-9 px-5 ml-auto"
                    data-testid="media-buy-btn"
                  >
                    {purchasing === 'buy'
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <Plus className="w-4 h-4 mr-2" />}
                    {tr('Get', 'აღება')} {clampGb(buyGb)} GB · {status.currency} {formatMoney(pricePerGb * clampGb(buyGb))}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Vault controls: share link toggle + host upload + download all */}
        <div className="glass-card rounded-bubble p-4 sm:p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-[220px]">
              <div className="flex items-center gap-3">
                <Switch
                  checked={!!status?.guest_upload_enabled}
                  onCheckedChange={toggleGuestUploads}
                  disabled={togglingShare || !status}
                  data-testid="media-guest-toggle"
                />
                <Label className="text-sm font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
                  {tr('Guest uploads via share link', 'სტუმრების ატვირთვა ბმულით')}
                </Label>
              </div>
              <p className="text-xs text-[#4a3d66] dark:text-[#c3b8dd] mt-1.5 max-w-md">
                {tr(
                  'Guests scan or open the link and add photos/videos from their phone — no account needed. Files go straight to secure storage.',
                  'სტუმრები ხსნიან ბმულს და ამატებენ ფოტო/ვიდეოს ტელეფონიდან — ანგარიშის გარეშე. ფაილები პირდაპირ დაცულ საცავში მიდის.',
                )}
              </p>
              {status?.guest_upload_enabled && shareUrl && (
                <button
                  type="button"
                  onClick={copyShareLink}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#1b1033]/5 dark:bg-white/10 px-3 py-1.5 text-xs font-medium text-[#4a3d66] dark:text-[#c3b8dd] hover:bg-[#1b1033]/10 dark:hover:bg-white/15 max-w-full"
                  data-testid="media-copy-share-link"
                >
                  <Copy className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{shareUrl}</span>
                </button>
              )}
            </div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={!!uploadState || !status?.service_active}
                className="rounded-full h-10 px-5"
                data-testid="media-host-upload-btn"
              >
                {uploadState ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {tr('Uploading', 'იტვირთება')} {uploadState.done + 1}/{uploadState.total}
                    {uploadState.percent ? ` · ${uploadState.percent}%` : ''}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {tr('Upload', 'ატვირთვა')}
                  </>
                )}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept={MEDIA_ACCEPT}
                multiple
                className="hidden"
                onChange={(e) => handleHostFiles(e.target.files)}
                data-testid="media-host-input"
              />
              <Button
                onClick={handleDownloadAll}
                disabled={archiving || total === 0}
                className="btn-gradient text-white rounded-full h-10 px-5 shadow-celebrate-sm"
                data-testid="media-download-all"
              >
                {archiving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {tr('Download all', 'ყველას ჩამოტვირთვა')}
              </Button>
            </div>
          </div>
          {status?.service_active && (
            <p className="text-xs text-[#4a3d66] dark:text-[#c3b8dd] mt-3 tabular-nums" data-testid="media-usage">
              {status.photo_count + status.video_count} {tr('items', 'ფაილი')} ·{' '}
              {formatBytes(status.used_bytes)} / {formatBytes(status.quota_bytes)}
            </p>
          )}
        </div>

        <div className="glass-card rounded-bubble overflow-hidden">
          {/* Tabs + select toggle */}
          <div className="flex items-center justify-between gap-2 p-4 sm:px-6 border-b border-[#1b1033]/10 dark:border-white/10 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {tabs.map(({ id, label, count, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    tab === id
                      ? 'btn-gradient text-white shadow-celebrate-sm'
                      : 'bg-[#1b1033]/5 dark:bg-white/10 text-[#4a3d66] dark:text-[#c3b8dd] hover:bg-[#1b1033]/10 dark:hover:bg-white/15'
                  }`}
                  data-testid={`media-tab-${id}`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                  <span className={`rounded-full px-2 py-0.5 text-xs tabular-nums ${tab === id ? 'bg-white/20' : 'bg-[#1b1033]/5 dark:bg-white/10'}`}>
                    {count}
                  </span>
                </button>
              ))}
            </div>
            <Button
              variant={selectMode ? 'default' : 'outline'}
              onClick={() => (selectMode ? clearSelection() : setSelectMode(true))}
              className={`rounded-full h-9 px-4 ${selectMode ? 'btn-gradient text-white' : ''}`}
              data-testid="media-select-toggle"
            >
              <ListChecks className="w-4 h-4 mr-2" />
              {selectMode ? tr('Cancel', 'გაუქმება') : tr('Select', 'მონიშვნა')}
            </Button>
          </div>

          {/* Folder chips */}
          {(folders.length > 0 || unsortedCount > 0) && (
            <div className="flex items-center gap-2 p-3 sm:px-6 border-b border-[#1b1033]/10 dark:border-white/10 flex-wrap" data-testid="media-folder-chips">
              {[
                { key: null as string | null, label: tr('All', 'ყველა'), count: unsortedCount + folders.reduce((s, f) => s + f.count, 0) },
                ...(unsortedCount > 0 ? [{ key: MEDIA_UNSORTED, label: tr('Unsorted', 'დაუხარისხებელი'), count: unsortedCount }] : []),
                ...folders.map((f) => ({ key: f.name, label: f.name, count: f.count })),
              ].map((chip) => (
                <button
                  key={chip.key ?? '__all__'}
                  type="button"
                  onClick={() => setFolderFilter(chip.key)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    folderFilter === chip.key
                      ? 'bg-[#ff4d8d] text-white'
                      : 'bg-[#1b1033]/5 dark:bg-white/10 text-[#4a3d66] dark:text-[#c3b8dd] hover:bg-[#1b1033]/10 dark:hover:bg-white/15'
                  }`}
                  data-testid={`media-folder-chip-${chip.key ?? 'all'}`}
                >
                  <Folder className="w-3.5 h-3.5" />
                  {chip.label}
                  <span className="tabular-nums opacity-70">{chip.count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="p-4 sm:px-6 flex items-center gap-3 flex-wrap border-b border-[#1b1033]/10 dark:border-white/10">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a3d66] dark:text-[#c3b8dd]" />
              <Input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={tr('Search by uploader name or file…', 'ძებნა ამტვირთავის სახელით ან ფაილით…')}
                className="pl-10 h-10 rounded-full"
                data-testid="media-search"
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => setSearchText('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4a3d66] dark:text-[#c3b8dd] hover:text-[#ff4d8d]"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Select value={uploadedBy} onValueChange={(v: any) => setUploadedBy(v)}>
              <SelectTrigger className="w-[160px] h-10 rounded-full" data-testid="media-uploader-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{tr('Everyone', 'ყველა')}</SelectItem>
                <SelectItem value="host">{tr('You (host)', 'თქვენ (მასპინძელი)')}</SelectItem>
                <SelectItem value="guest">{tr('Guests', 'სტუმრები')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={(v: any) => setSort(v)}>
              <SelectTrigger className="w-[170px] h-10 rounded-full" data-testid="media-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{tr('Newest first', 'ჯერ ახლები')}</SelectItem>
                <SelectItem value="oldest">{tr('Oldest first', 'ჯერ ძველები')}</SelectItem>
                <SelectItem value="largest">{tr('Largest first', 'ჯერ დიდები')}</SelectItem>
                <SelectItem value="uploader">{tr('By uploader name', 'ამტვირთავის სახელით')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Selection action bar */}
          {selectMode && (
            <div className="flex items-center gap-2 p-3 sm:px-6 border-b border-[#1b1033]/10 dark:border-white/10 flex-wrap bg-[#fdeee9]/60 dark:bg-[#281b47]/40" data-testid="media-selection-bar">
              <span className="text-sm font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
                {language === 'ka' ? `${selected.size} მონიშნული` : `${selected.size} selected`}
              </span>
              <button
                type="button"
                onClick={() => setSelected(new Set(items.map((i) => i.media_id)))}
                className="text-xs font-medium text-[#ff4d8d] hover:underline"
                data-testid="media-select-all"
              >
                {tr('Select all here', 'ყველას მონიშვნა')}
              </button>
              <div className="flex items-center gap-2 ml-auto flex-wrap">
                <div className="relative">
                  <Button
                    variant="outline"
                    onClick={() => setShowMoveMenu((v) => !v)}
                    disabled={selected.size === 0 || moving}
                    className="rounded-full h-9 px-4"
                    data-testid="media-move-btn"
                  >
                    {moving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderInput className="w-4 h-4 mr-2" />}
                    {tr('Move to folder', 'საქაღალდეში')}
                  </Button>
                  {showMoveMenu && (
                    <div className="absolute right-0 z-20 mt-2 w-56 rounded-2xl border border-[#1b1033]/10 dark:border-white/10 bg-white dark:bg-[#1e1438] shadow-celebrate p-2 max-h-72 overflow-auto" data-testid="media-move-menu">
                      <button
                        type="button"
                        onClick={() => {
                          const name = window.prompt(tr('New folder name', 'ახალი საქაღალდის სახელი'))?.trim()
                          if (name) moveSelected(name)
                        }}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-left text-[#1b1033] dark:text-[#f4f0ff] hover:bg-[#1b1033]/5 dark:hover:bg-white/10"
                      >
                        <Plus className="w-4 h-4" /> {tr('New folder…', 'ახალი საქაღალდე…')}
                      </button>
                      {folders.map((f) => (
                        <button
                          key={f.name}
                          type="button"
                          onClick={() => moveSelected(f.name)}
                          className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-left text-[#1b1033] dark:text-[#f4f0ff] hover:bg-[#1b1033]/5 dark:hover:bg-white/10"
                        >
                          <Folder className="w-4 h-4 shrink-0" /> <span className="truncate">{f.name}</span>
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => moveSelected(null)}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-left text-[#4a3d66] dark:text-[#c3b8dd] hover:bg-[#1b1033]/5 dark:hover:bg-white/10"
                      >
                        <X className="w-4 h-4" /> {tr('Remove from folder', 'ამოღება საქაღალდიდან')}
                      </button>
                    </div>
                  )}
                </div>
                <Button
                  onClick={downloadSelected}
                  disabled={selected.size === 0 || archiving}
                  className="btn-gradient text-white rounded-full h-9 px-4"
                  data-testid="media-download-selected"
                >
                  {archiving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  {tr('Download zip', 'ZIP')}
                </Button>
              </div>
            </div>
          )}

          {/* Grid */}
          <div className="p-4 sm:p-6">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-7 h-7 animate-spin text-[#ff4d8d]" />
              </div>
            ) : items.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[#1b1033]/15 dark:border-white/15 py-16 text-center text-sm text-[#4a3d66] dark:text-[#c3b8dd]" data-testid="media-empty">
                {query || uploadedBy !== 'all' || folderFilter
                  ? tr('Nothing matches these filters', 'ამ ფილტრებით ვერაფერი მოიძებნა')
                  : tab === 'video'
                    ? tr('No videos yet — share the upload link with your guests', 'ვიდეოები ჯერ არ არის — გაუზიარეთ ბმული სტუმრებს')
                    : tr('No photos yet — share the upload link with your guests', 'ფოტოები ჯერ არ არის — გაუზიარეთ ბმული სტუმრებს')}
              </div>
            ) : (
              <>
                <p className="text-xs text-[#4a3d66] dark:text-[#c3b8dd] mb-3 tabular-nums" data-testid="media-total">
                  {language === 'ka' ? `${total} ფაილი` : `${total} item${total === 1 ? '' : 's'}`}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5" data-testid="media-grid">
                  {items.map((item) => {
                    const isVideo = item.content_type?.startsWith('video/')
                    const tile = item.thumb_url || (isVideo ? null : item.view_url)
                    return (
                      <button
                        key={item.media_id}
                        type="button"
                        onClick={() => (selectMode ? toggleSelect(item.media_id) : setLightbox(item))}
                        className={`group relative aspect-square overflow-hidden rounded-xl border bg-[#1b1033]/5 dark:bg-white/5 focus:outline-none focus:ring-2 focus:ring-[#ff4d8d] ${
                          selected.has(item.media_id)
                            ? 'border-[#ff4d8d] ring-2 ring-[#ff4d8d]'
                            : 'border-[#1b1033]/10 dark:border-white/10'
                        }`}
                        data-testid={`media-item-${item.media_id}`}
                      >
                        {tile ? (
                          <img
                            src={tile}
                            alt={item.original_filename || ''}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                          />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center">
                            <Film className="w-8 h-8 text-[#4a3d66]/50 dark:text-[#c3b8dd]/50" />
                          </span>
                        )}
                        {selectMode && (
                          <span
                            className={`absolute top-1.5 left-1.5 flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                              selected.has(item.media_id) ? 'bg-[#ff4d8d] border-[#ff4d8d] text-white' : 'bg-black/30 border-white/80'
                            }`}
                          >
                            {selected.has(item.media_id) && <CheckCircle2 className="w-4 h-4" />}
                          </span>
                        )}
                        {item.folder && !selectMode && (
                          <span className="absolute top-1.5 left-1.5 inline-flex max-w-[70%] items-center gap-1 truncate rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur">
                            <Folder className="w-2.5 h-2.5 shrink-0" /> {item.folder}
                          </span>
                        )}
                        {isVideo && <Film className="absolute bottom-1.5 left-1.5 w-4 h-4 text-white drop-shadow" />}
                        <span className="absolute bottom-1.5 right-1.5 max-w-[80%] truncate rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur">
                          {item.uploaded_by === 'guest'
                            ? item.uploader_name || tr('Guest', 'სტუმარი')
                            : tr('You', 'თქვენ')}
                        </span>
                      </button>
                    )
                  })}
                </div>
                {hasMore && (
                  <div className="mt-5 text-center">
                    <Button
                      variant="outline"
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="rounded-full h-10 px-6"
                      data-testid="media-load-more"
                    >
                      {loadingMore && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {tr('Load more', 'მეტის ჩატვირთვა')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent className="sm:max-w-3xl p-0 overflow-hidden" aria-describedby={undefined}>
          {lightbox && (
            <div>
              <DialogTitle className="sr-only">{lightbox.original_filename || 'media'}</DialogTitle>
              <div className="bg-black flex items-center justify-center max-h-[70vh]">
                {lightbox.content_type?.startsWith('video/') ? (
                  <video
                    src={lightbox.view_url}
                    poster={lightbox.thumb_url || undefined}
                    className="max-h-[70vh] w-full object-contain"
                    controls
                    playsInline
                  />
                ) : (
                  <img
                    src={lightbox.view_url}
                    alt={lightbox.original_filename || ''}
                    className="max-h-[70vh] w-full object-contain"
                  />
                )}
              </div>
              <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 text-sm">
                  <p className="font-medium text-[#1b1033] dark:text-[#f4f0ff] truncate flex items-center gap-1.5">
                    {lightbox.uploaded_by === 'guest' ? <Users className="w-4 h-4" /> : <User className="w-4 h-4" />}
                    {lightbox.uploaded_by === 'guest'
                      ? (lightbox.uploader_name || tr('Guest', 'სტუმარი'))
                      : tr('You (host)', 'თქვენ (მასპინძელი)')}
                  </p>
                  <p className="text-xs text-[#4a3d66] dark:text-[#c3b8dd] truncate">
                    {[lightbox.original_filename, formatWhen(lightbox.created_at), formatBytes(lightbox.size_bytes)]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <div className="flex items-center gap-2.5">
                  {lightbox.download_url && (
                    <a href={lightbox.download_url} download data-testid="media-download-btn">
                      <Button variant="outline" size="sm" className="rounded-full">
                        <Download className="w-4 h-4 mr-1.5" />
                        {tr('Download', 'ჩამოტვირთვა')}
                      </Button>
                    </a>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(lightbox)}
                    disabled={deleting}
                    className="rounded-full text-red-600 border-red-200 hover:bg-red-50 dark:border-red-500/30 dark:hover:bg-red-500/10"
                    data-testid="media-delete-btn"
                  >
                    {deleting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
                    {tr('Delete', 'წაშლა')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AppFooter />
    </div>
  )
}

export default MediaGallery
